import { useState, useEffect } from "react";
import { api } from "@/App";
import { Search, X } from "lucide-react";

// Instagram-style user picker: search → select → callback
export const UserSearchModal = ({ open, onClose, onSelect, title }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setQuery(""); setResults([]); return; }
  }, [open]);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const id = setTimeout(() => {
      setLoading(true);
      api.get(`/users/search?q=${encodeURIComponent(query.trim())}`)
        .then((r) => setResults(r.data.users || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(id);
  }, [query]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center" data-testid="user-search-modal">
      <div className="bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl max-h-[75dvh] flex flex-col" style={{ paddingBottom: "max(env(safe-area-inset-bottom), 12px)" }}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h3 className="font-bold text-gray-900">{title}</h3>
          <button onClick={onClose} className="p-2 rounded-full bg-gray-100" data-testid="user-search-close">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="px-4 pb-2">
          <div className="flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2.5">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca utente..."
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
              data-testid="user-search-input"
            />
          </div>
        </div>
        <div className="overflow-y-auto px-3 pb-3 flex-1">
          {loading && <p className="text-center text-gray-400 text-sm py-4">...</p>}
          {!loading && query.trim().length >= 2 && results.length === 0 && (
            <p className="text-center text-gray-400 text-sm py-6">Nessun utente trovato</p>
          )}
          {results.map((u) => (
            <button
              key={u.user_id}
              onClick={() => onSelect(u)}
              className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-gray-50 text-left"
              data-testid={`user-result-${u.user_id}`}
            >
              <img
                src={u.picture ? (u.picture.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u.picture}` : u.picture) : "/images/default-avatar.png"}
                alt=""
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <p className="text-sm font-semibold text-gray-900">{u.name}</p>
                {u.username && <p className="text-xs text-gray-400">@{u.username}</p>}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
